import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { jobsAPI, customersAPI, excavatorsAPI, usersAPI, exportAPI, jobTypesAPI } from "../lib/api";
import { compressImage } from "../lib/imageUtils";
import { formatDate } from "../lib/dateUtils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { DatePicker } from "../components/DatePicker";
import { SearchableSelect } from "../components/SearchableSelect";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Plus,
  Search,
  MapPin,
  Calendar,
  User,
  Truck,
  Filter,
  Eye,
  Edit,
  Trash2,
  Download,
  Clock,
  DollarSign,
  Car,
  Image,
  X,
  Settings,
  Phone,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

// Scheduled time options
const TIME_SLOTS = ["0830", "1030", "1230", "1430", "1630", "1830"];

const formatTimeSlot = (time) => {
  if (!time) return "";
  return `${time.slice(0, 2)}:${time.slice(2)}`;
};

const Jobs = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [excavators, setExcavators] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [jobTypes, setJobTypes] = useState({ predefined: [], custom: [], all: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Sorting
  const [sortBy, setSortBy] = useState("scheduled_date");
  const [sortOrder, setSortOrder] = useState("desc");
  
  // Job type management
  const [newJobType, setNewJobType] = useState("");
  const [jobTypeDialogOpen, setJobTypeDialogOpen] = useState(false);
  const [manageJobTypesDialogOpen, setManageJobTypesDialogOpen] = useState(false);
  const [editingJobType, setEditingJobType] = useState(null);
  const [editJobTypeName, setEditJobTypeName] = useState("");
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  
  const [formData, setFormData] = useState({
    customer_id: "",
    excavator_id: "",
    description: "",
    vehicle_number: "",
    assigned_mechanic_id: "",
    location_address: "",
    scheduled_date: "",
    scheduled_time: "",
    job_type_tags: [],
    billing_type: "chargeable",
    site_contact_person: "",
    site_contact_number: "",
  });

  const canCreateJob = user?.role === "admin" || user?.role === "manager";
  const canEditJob = user?.role === "admin";
  const canDeleteJob = user?.role === "admin";
  const canExport = user?.role === "admin" || user?.role === "manager";
  const canAddJobTypes = user?.role === "admin" || user?.role === "manager";
  const canEditJobTypes = user?.role === "admin" || user?.role === "manager";
  const canDeleteJobTypes = user?.role === "admin" || user?.role === "manager";

  // Sort jobs client-side
  const sortedJobs = React.useMemo(() => {
    if (!jobs.length) return jobs;
    
    return [...jobs].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case "scheduled_date":
          aVal = a.scheduled_date || "";
          bVal = b.scheduled_date || "";
          break;
        case "customer_name":
          aVal = (a.customer_name || "").toLowerCase();
          bVal = (b.customer_name || "").toLowerCase();
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "created_at":
          aVal = a.created_at || "";
          bVal = b.created_at || "";
          break;
        default:
          aVal = a.scheduled_date || "";
          bVal = b.scheduled_date || "";
      }
      
      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  }, [jobs, sortBy, sortOrder]);

  // Toggle sort
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  // Fetch jobs with filters
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
      };
      
      if (statusFilter !== "all") params.status = statusFilter;
      if (customerFilter !== "all") params.customer_id = customerFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (searchTerm) params.search = searchTerm;

      const res = await jobsAPI.getAll(params);
      setJobs(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (error) {
      toast.error("Failed to fetch jobs");
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, customerFilter, dateFrom, dateTo, searchTerm]);

  // Fetch reference data
  const fetchReferenceData = useCallback(async () => {
    try {
      const [customersRes, excavatorsRes, mechanicsRes, jobTypesRes] = await Promise.all([
        customersAPI.getAll(),
        excavatorsAPI.getAll(),
        usersAPI.getMechanics(),
        jobTypesAPI.getAll(),
      ]);
      setCustomers(customersRes.data);
      setExcavators(excavatorsRes.data);
      setMechanics(mechanicsRes.data);
      setJobTypes(jobTypesRes.data);
    } catch (error) {
      toast.error("Failed to fetch reference data");
    }
  }, []);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, customerFilter, dateFrom, dateTo, searchTerm]);

  const resetForm = useCallback(() => {
    setFormData({
      customer_id: "",
      excavator_id: "",
      description: "",
      vehicle_number: "",
      assigned_mechanic_id: "",
      location_address: "",
      scheduled_date: "",
      scheduled_time: "",
      job_type_tags: [],
      billing_type: "chargeable",
      site_contact_person: "",
      site_contact_number: "",
    });
    setUploadedPhotos([]);
  }, []);

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setCustomerFilter("all");
    setDateFrom("");
    setDateTo("");
  };

  const hasActiveFilters = statusFilter !== "all" || customerFilter !== "all" || dateFrom || dateTo || searchTerm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for required fields (except description)
    if (!formData.customer_id) {
      toast.error("Please select a customer");
      return;
    }
    if (!formData.excavator_id) {
      toast.error("Please select an excavator");
      return;
    }
    if (!formData.job_type_tags || formData.job_type_tags.length === 0) {
      toast.error("Please select at least one job type");
      return;
    }
    if (!formData.site_contact_person?.trim()) {
      toast.error("Please enter a site contact person name");
      return;
    }
    if (!formData.site_contact_number?.trim()) {
      toast.error("Please enter a site contact number");
      return;
    }
    if (!formData.vehicle_number?.trim()) {
      toast.error("Please enter a vehicle number");
      return;
    }
    if (!formData.assigned_mechanic_id) {
      toast.error("Please assign a mechanic");
      return;
    }
    if (!formData.location_address?.trim()) {
      toast.error("Please enter a location address");
      return;
    }
    if (!formData.scheduled_date) {
      toast.error("Please select a date");
      return;
    }
    if (!formData.scheduled_time) {
      toast.error("Please select a time slot");
      return;
    }
    if (!editingJob && uploadedPhotos.length === 0) {
      toast.error("Please upload at least one photo");
      return;
    }

    try {
      const submitData = {
        ...formData,
        photos: uploadedPhotos,
      };
      
      if (editingJob) {
        await jobsAPI.update(editingJob.id, submitData);
        toast.success("Job updated");
      } else {
        await jobsAPI.create(submitData);
        toast.success("Job created");
      }
      setDialogOpen(false);
      setEditingJob(null);
      resetForm();
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      customer_id: job.customer_id,
      excavator_id: job.excavator_id,
      description: job.description || "",
      vehicle_number: job.vehicle_number || "",
      assigned_mechanic_id: job.assigned_mechanic_id || "",
      location_address: job.location_address || "",
      scheduled_date: job.scheduled_date || "",
      scheduled_time: job.scheduled_time || "",
      job_type_tags: job.job_type_tags || [],
      billing_type: job.billing_type || "chargeable",
      site_contact_person: job.site_contact_person || "",
      site_contact_number: job.site_contact_number || "",
    });
    setUploadedPhotos(job.photos || []);
    setDialogOpen(true);
  };

  const handleDelete = async (jobId) => {
    try {
      await jobsAPI.delete(jobId);
      toast.success("Job deleted");
      fetchJobs();
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const handleExport = async () => {
    try {
      toast.info("Preparing export...");
      await exportAPI.download("jobs");
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleEditJobType = async () => {
    if (!editJobTypeName.trim() || !editingJobType) return;
    try {
      await jobTypesAPI.update(editingJobType, editJobTypeName.trim());
      toast.success("Job type updated");
      setEditingJobType(null);
      setEditJobTypeName("");
      const jobTypesRes = await jobTypesAPI.getAll();
      setJobTypes(jobTypesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update job type");
    }
  };

  const handleDeleteJobType = async (typeName) => {
    try {
      await jobTypesAPI.delete(typeName);
      toast.success("Job type deleted");
      const jobTypesRes = await jobTypesAPI.getAll();
      setJobTypes(jobTypesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete job type");
    }
  };

  const openInGoogleMaps = () => {
    if (!formData.location_address) {
      toast.error("Please enter an address first");
      return;
    }
    const encodedAddress = encodeURIComponent(formData.location_address);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(googleMapsUrl, '_blank');
  };

  const handleJobTypeToggle = (jobType) => {
    const current = formData.job_type_tags;
    if (current.includes(jobType)) {
      setFormData({ ...formData, job_type_tags: current.filter((t) => t !== jobType) });
    } else {
      setFormData({ ...formData, job_type_tags: [...current, jobType] });
    }
  };

  const handleAddCustomJobType = async () => {
    if (!newJobType.trim()) return;
    try {
      await jobTypesAPI.add(newJobType.trim());
      toast.success("Job type added");
      setNewJobType("");
      setJobTypeDialogOpen(false);
      const jobTypesRes = await jobTypesAPI.getAll();
      setJobTypes(jobTypesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add job type");
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    if (uploadedPhotos.length + files.length > 20) {
      toast.error(`Maximum 20 photos allowed. You can add ${20 - uploadedPhotos.length} more.`);
      return;
    }

    for (const file of files) {
      try {
        const compressed = await compressImage(file, 200);
        setUploadedPhotos((prev) => [...prev, compressed]);
      } catch (error) {
        toast.error("Failed to process image");
      }
    }
  };

  const handleRemovePhoto = (index) => {
    setUploadedPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-800 border-amber-300",
      in_progress: "bg-blue-100 text-blue-800 border-blue-300",
      completed: "bg-green-100 text-green-800 border-green-300",
    };
    return (
      <Badge className={`${styles[status] || styles.pending} rounded-none uppercase text-xs font-bold tracking-wide border`}>
        {status?.replace("_", " ")}
      </Badge>
    );
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="jobs-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Jobs
          </h1>
          <p className="text-slate-500">
            {total} {total === 1 ? "job" : "jobs"} found
          </p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <Button
              variant="outline"
              onClick={handleExport}
              className="rounded-sm"
              data-testid="export-jobs-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
          {canCreateJob && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditingJob(null);
                resetForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-blue-500 text-black font-bold uppercase tracking-wider hover:bg-blue-600 rounded-sm border-2 border-transparent hover:border-black"
                  data-testid="create-job-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Job
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl font-bold uppercase">
                    {editingJob ? "Edit Job" : "Create New Job"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Customer & Excavator */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="font-semibold uppercase text-xs tracking-wide">Customer *</Label>
                      <SearchableSelect
                        value={formData.customer_id}
                        onChange={(v) => setFormData({ ...formData, customer_id: v })}
                        options={customers}
                        placeholder="Select customer"
                        searchPlaceholder="Search customers..."
                        displayKey="company"
                        valueKey="id"
                        data-testid="job-customer-select"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="font-semibold uppercase text-xs tracking-wide">Excavator *</Label>
                      <SearchableSelect
                        value={formData.excavator_id}
                        onChange={(v) => setFormData({ ...formData, excavator_id: v })}
                        options={excavators.map(e => ({ id: e.id, name: `${e.model} - ${e.serial_number}` }))}
                        placeholder="Select excavator"
                        searchPlaceholder="Search excavators..."
                        displayKey="name"
                        valueKey="id"
                        data-testid="job-excavator-select"
                      />
                    </div>
                  </div>

                  {/* Job Type Checkboxes */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="font-semibold uppercase text-xs tracking-wide">Job Type *</Label>
                      <div className="flex gap-1">
                        {canAddJobTypes && (
                          <Dialog open={jobTypeDialogOpen} onOpenChange={setJobTypeDialogOpen}>
                            <DialogTrigger asChild>
                              <Button type="button" variant="ghost" size="sm" className="h-6 text-xs">
                                <Plus className="w-3 h-3 mr-1" />
                                Add
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-sm">
                              <DialogHeader>
                                <DialogTitle className="font-heading uppercase">Add Custom Job Type</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <Input
                                  value={newJobType}
                                  onChange={(e) => setNewJobType(e.target.value)}
                                  placeholder="Enter job type name"
                                  className="rounded-sm"
                                  data-testid="new-job-type-input"
                                />
                                <Button
                                  type="button"
                                  onClick={handleAddCustomJobType}
                                  className="w-full bg-blue-500 text-black font-bold uppercase hover:bg-blue-600 rounded-sm"
                                  data-testid="add-job-type-btn"
                                >
                                  Add Job Type
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                        {canEditJobTypes && (
                          <Dialog open={manageJobTypesDialogOpen} onOpenChange={setManageJobTypesDialogOpen}>
                            <DialogTrigger asChild>
                              <Button type="button" variant="ghost" size="sm" className="h-6 text-xs">
                                <Settings className="w-3 h-3 mr-1" />
                                Manage
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-md">
                              <DialogHeader>
                                <DialogTitle className="font-heading uppercase">Manage Job Types</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-2 max-h-80 overflow-y-auto">
                                {jobTypes.all.map((type) => (
                                  <div key={type} className="flex items-center justify-between p-2 bg-slate-50 rounded-sm border">
                                    {editingJobType === type ? (
                                      <div className="flex gap-2 flex-1">
                                        <Input
                                          value={editJobTypeName}
                                          onChange={(e) => setEditJobTypeName(e.target.value)}
                                          className="rounded-sm h-8 text-sm"
                                          autoFocus
                                        />
                                        <Button
                                          type="button"
                                          size="sm"
                                          onClick={handleEditJobType}
                                          className="h-8 bg-green-500 hover:bg-green-600"
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="outline"
                                          onClick={() => { setEditingJobType(null); setEditJobTypeName(""); }}
                                          className="h-8"
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    ) : (
                                      <>
                                        <span className="text-sm">{type}</span>
                                        <div className="flex gap-1">
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => { setEditingJobType(type); setEditJobTypeName(type); }}
                                            className="h-7 w-7 p-0"
                                          >
                                            <Edit className="w-3 h-3" />
                                          </Button>
                                          <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                              <AlertDialogHeader>
                                                <AlertDialogTitle>Delete Job Type?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                  Are you sure you want to delete "{type}"? This cannot be undone.
                                                </AlertDialogDescription>
                                              </AlertDialogHeader>
                                              <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction
                                                  onClick={() => handleDeleteJobType(type)}
                                                  className="bg-red-500 hover:bg-red-600"
                                                >
                                                  Delete
                                                </AlertDialogAction>
                                              </AlertDialogFooter>
                                            </AlertDialogContent>
                                          </AlertDialog>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 bg-slate-50 rounded-sm border border-slate-200">
                      {jobTypes.all.map((type) => (
                        <div key={type} className="flex items-center space-x-2">
                          <Checkbox
                            id={`type-${type}`}
                            checked={formData.job_type_tags.includes(type)}
                            onCheckedChange={() => handleJobTypeToggle(type)}
                            data-testid={`job-type-${type.toLowerCase().replace(/\s/g, '-')}`}
                          />
                          <label
                            htmlFor={`type-${type}`}
                            className="text-sm cursor-pointer"
                          >
                            {type}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Billing Type Radio */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">Billing Type</Label>
                    <RadioGroup
                      value={formData.billing_type}
                      onValueChange={(v) => setFormData({ ...formData, billing_type: v })}
                      className="flex gap-4 flex-wrap"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="chargeable" id="chargeable" data-testid="billing-chargeable" />
                        <Label htmlFor="chargeable" className="cursor-pointer flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          Chargeable
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="partial" id="partial" data-testid="billing-partial" />
                        <Label htmlFor="partial" className="cursor-pointer text-amber-600">
                          Partial
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="non-chargeable" id="non-chargeable" data-testid="billing-non-chargeable" />
                        <Label htmlFor="non-chargeable" className="cursor-pointer">
                          Non-chargeable
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Description (optional) */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">Description (Optional)</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Additional details about the repair work..."
                      className="rounded-sm"
                      data-testid="job-description"
                    />
                  </div>

                  {/* Site Contact Person - Split into Name and Number */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="font-semibold uppercase text-xs tracking-wide flex items-center gap-1">
                        <User className="w-4 h-4" />
                        Site Contact Name *
                      </Label>
                      <Input
                        value={formData.site_contact_person}
                        onChange={(e) => setFormData({ ...formData, site_contact_person: e.target.value })}
                        placeholder="Contact person name"
                        className="rounded-sm"
                        data-testid="job-site-contact-name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-semibold uppercase text-xs tracking-wide flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        Site Contact Number *
                      </Label>
                      <Input
                        value={formData.site_contact_number}
                        onChange={(e) => setFormData({ ...formData, site_contact_number: e.target.value })}
                        placeholder="Contact phone number"
                        className="rounded-sm"
                        data-testid="job-site-contact-number"
                        required
                      />
                    </div>
                  </div>

                  {/* Vehicle Number & Mechanic */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="font-semibold uppercase text-xs tracking-wide flex items-center gap-1">
                        <Car className="w-4 h-4" />
                        Vehicle Number *
                      </Label>
                      <Input
                        value={formData.vehicle_number}
                        onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })}
                        placeholder="e.g., SGX1234A"
                        className="rounded-sm"
                        data-testid="job-vehicle-number"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="font-semibold uppercase text-xs tracking-wide">Mechanic *</Label>
                      <Select
                        value={formData.assigned_mechanic_id}
                        onValueChange={(v) => setFormData({ ...formData, assigned_mechanic_id: v === "none" ? "" : v })}
                      >
                        <SelectTrigger data-testid="job-mechanic-select">
                          <SelectValue placeholder="Assign mechanic" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Unassigned</SelectItem>
                          {mechanics.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Location with Google Maps */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      Location *
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.location_address}
                        onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
                        placeholder="Job site address"
                        className="rounded-sm flex-1"
                        data-testid="job-location"
                        required
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={openInGoogleMaps}
                        className="rounded-sm bg-blue-50 hover:bg-blue-100 border-blue-200 px-2"
                        data-testid="open-google-maps-btn"
                        title="Open in Google Maps"
                      >
                        <MapPin className="w-4 h-4 text-blue-600" />
                        <ExternalLink className="w-3 h-3 ml-1 text-blue-600" />
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">Enter address and click the pin icon to open in Google Maps</p>
                  </div>

                  {/* Scheduled Date & Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="font-semibold uppercase text-xs tracking-wide flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Date *
                      </Label>
                      <DatePicker
                        value={formData.scheduled_date}
                        onChange={(date) => setFormData({ ...formData, scheduled_date: date })}
                        placeholder="Select date (dd/mm/yyyy)"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-semibold uppercase text-xs tracking-wide flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Time Slot *
                      </Label>
                      <RadioGroup
                        value={formData.scheduled_time}
                        onValueChange={(v) => setFormData({ ...formData, scheduled_time: v })}
                        className="grid grid-cols-3 gap-1"
                      >
                        {TIME_SLOTS.map((time) => (
                          <div key={time} className="flex items-center">
                            <RadioGroupItem value={time} id={`time-${time}`} className="peer sr-only" />
                            <Label
                              htmlFor={`time-${time}`}
                              className="flex items-center justify-center px-2 py-1.5 text-xs border rounded-sm cursor-pointer bg-white hover:bg-slate-50 peer-data-[state=checked]:bg-blue-500 peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:font-bold"
                              data-testid={`time-slot-${time}`}
                            >
                              {formatTimeSlot(time)}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  </div>

                  {/* Photo Upload */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide flex items-center gap-1">
                      <Image className="w-4 h-4" />
                      Photos * ({uploadedPhotos.length}/20)
                    </Label>
                    <div className="flex gap-2 flex-wrap">
                      {uploadedPhotos.map((photo, index) => (
                        <div key={index} className="relative w-16 h-16">
                          <img
                            src={photo}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-full object-cover rounded-sm border border-slate-200"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(index)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {uploadedPhotos.length < 20 && (
                        <label className="w-16 h-16 border-2 border-dashed border-slate-300 rounded-sm flex items-center justify-center cursor-pointer hover:border-blue-500">
                          <Input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handlePhotoUpload}
                            className="hidden"
                            data-testid="job-photo-upload"
                          />
                          <Plus className="w-6 h-6 text-slate-400" />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">At least one photo is required</p>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      className="flex-1 rounded-sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-blue-500 text-black font-bold uppercase hover:bg-blue-600 rounded-sm"
                      data-testid="job-submit"
                    >
                      {editingJob ? "Update" : "Create"} Job
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by customer, location, excavator, description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-sm"
              data-testid="jobs-search"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className={`rounded-sm ${filtersExpanded ? 'bg-slate-100' : ''}`}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <Badge className="ml-2 bg-blue-600 text-black text-xs">Active</Badge>
            )}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="rounded-sm text-slate-500"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        {/* Expanded Filters */}
        {filtersExpanded && (
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Status Filter */}
                <div className="space-y-1">
                  <Label className="text-xs uppercase font-semibold text-slate-500">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="rounded-sm" data-testid="jobs-status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Customer Filter */}
                <div className="space-y-1">
                  <Label className="text-xs uppercase font-semibold text-slate-500">Customer</Label>
                  <Select value={customerFilter} onValueChange={setCustomerFilter}>
                    <SelectTrigger className="rounded-sm" data-testid="customer-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Customers</SelectItem>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date From */}
                <div className="space-y-1">
                  <Label className="text-xs uppercase font-semibold text-slate-500">Date From</Label>
                  <DatePicker
                    value={dateFrom}
                    onChange={(date) => setDateFrom(date)}
                    placeholder="From (dd/mm/yyyy)"
                  />
                </div>

                {/* Date To */}
                <div className="space-y-1">
                  <Label className="text-xs uppercase font-semibold text-slate-500">Date To</Label>
                  <DatePicker
                    value={dateTo}
                    onChange={(date) => setDateTo(date)}
                    placeholder="To (dd/mm/yyyy)"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sort Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-500 font-medium">Sort by:</span>
          <div className="flex gap-1">
            <Button
              variant={sortBy === "scheduled_date" ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort("scheduled_date")}
              className={`rounded-sm text-xs ${sortBy === "scheduled_date" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
            >
              Date
              {sortBy === "scheduled_date" && (
                sortOrder === "desc" ? <ArrowDown className="w-3 h-3 ml-1" /> : <ArrowUp className="w-3 h-3 ml-1" />
              )}
            </Button>
            <Button
              variant={sortBy === "customer_name" ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort("customer_name")}
              className={`rounded-sm text-xs ${sortBy === "customer_name" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
            >
              Customer
              {sortBy === "customer_name" && (
                sortOrder === "desc" ? <ArrowDown className="w-3 h-3 ml-1" /> : <ArrowUp className="w-3 h-3 ml-1" />
              )}
            </Button>
            <Button
              variant={sortBy === "status" ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort("status")}
              className={`rounded-sm text-xs ${sortBy === "status" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
            >
              Status
              {sortBy === "status" && (
                sortOrder === "desc" ? <ArrowDown className="w-3 h-3 ml-1" /> : <ArrowUp className="w-3 h-3 ml-1" />
              )}
            </Button>
            <Button
              variant={sortBy === "created_at" ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort("created_at")}
              className={`rounded-sm text-xs ${sortBy === "created_at" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
            >
              Created
              {sortBy === "created_at" && (
                sortOrder === "desc" ? <ArrowDown className="w-3 h-3 ml-1" /> : <ArrowUp className="w-3 h-3 ml-1" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Jobs List */}
      {sortedJobs.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Truck className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No jobs found</p>
            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                variant="link"
                className="mt-2 text-blue-600"
              >
                Clear filters to see all jobs
              </Button>
            )}
            {canCreateJob && !hasActiveFilters && (
              <Button
                onClick={() => setDialogOpen(true)}
                className="mt-4 bg-blue-500 text-black font-bold uppercase hover:bg-blue-600 rounded-sm"
              >
                Create First Job
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4">
            {sortedJobs.map((job) => (
              <Card key={job.id} className="border-slate-200 hover:border-slate-400 transition-colors" data-testid={`job-card-${job.id}`}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        {getStatusBadge(job.status)}
                        {job.vehicle_number && (
                          <Badge variant="outline" className="rounded-none text-xs flex items-center gap-1">
                            <Car className="w-3 h-3" />
                            {job.vehicle_number}
                          </Badge>
                        )}
                        {!job.is_chargeable && (
                          <Badge variant="outline" className="rounded-none text-xs bg-slate-100">
                            Non-chargeable
                          </Badge>
                        )}
                      </div>
                      {job.job_type_tags && job.job_type_tags.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {job.job_type_tags.map((tag) => (
                            <Badge key={tag} className="bg-yellow-100 text-yellow-800 border-yellow-300 rounded-none text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <h3 className="font-heading font-bold text-lg text-slate-900">
                        {job.description || `${job.excavator_model} Repair`}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>{job.customer_name || "No customer"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4" />
                          <span>
                            {job.excavator_model} - {job.excavator_serial}
                          </span>
                        </div>
                        {job.assigned_mechanic_name && (user?.role === "admin" || user?.role === "manager") && (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-blue-500" />
                            <span className="text-blue-600 font-medium">Mechanic: {job.assigned_mechanic_name}</span>
                          </div>
                        )}
                        {job.location_address && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span className="truncate max-w-xs">{job.location_address}</span>
                          </div>
                        )}
                        {(job.scheduled_date || job.scheduled_time) && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {job.scheduled_date && formatDate(job.scheduled_date)}
                              {job.scheduled_time && ` at ${formatTimeSlot(job.scheduled_time)}`}
                            </span>
                          </div>
                        )}
                      </div>
                      {job.is_chargeable && job.labour_charge > 0 && (
                        <div className="flex items-center gap-2 text-sm font-semibold text-green-700">
                          <DollarSign className="w-4 h-4" />
                          <span>Labour: ${job.labour_charge.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/jobs/${job.id}`}>
                        <Button
                          variant="outline"
                          className="rounded-sm border-2 hover:border-slate-900"
                          data-testid={`view-job-${job.id}`}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </Button>
                      </Link>
                      {canEditJob && (
                        <Button
                          variant="outline"
                          onClick={() => handleEdit(job)}
                          className="rounded-sm"
                          data-testid={`edit-job-${job.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      {canDeleteJob && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="rounded-sm text-red-500 hover:text-red-700"
                              data-testid={`delete-job-${job.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Job?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete this job. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(job.id)}
                                className="bg-red-500 hover:bg-red-600"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 pt-4">
              <p className="text-sm text-slate-500">
                Showing {(page - 1) * limit + 1} - {Math.min(page * limit, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="rounded-sm"
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <span className="text-sm text-slate-600 px-2">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  className="rounded-sm"
                  data-testid="next-page-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Jobs;
